import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setMac, setDeviceKey, getDeviceKey } from "@/lib/api";
import PlaylistForm from "@/components/PlaylistForm";
import { toast } from "sonner";
import { MonitorPlay, ShieldCheck, Loader2, Trash2, RefreshCw, ListVideo, CheckCircle2, Clock, CreditCard, Lock, Unlock, Eye, Pencil } from "lucide-react";

export default function Manage() {
  const [mac, setMacL] = useState("");
  const [key, setKey] = useState("");
  const [device, setDevice] = useState(null);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [currency, setCurrency] = useState("EUR");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [revealPw, setRevealPw] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [pinNew, setPinNew] = useState("");
  const [pinOld, setPinOld] = useState("");
  const navigate = useNavigate();

  const savePin = async () => {
    if (!/^\d{4}$/.test(pinNew)) { toast.error("PIN must be 4 digits"); return; }
    try { await api.post(`/device/${device.mac}/pin`, { pin: pinNew, old_pin: pinOld || undefined }); toast.success(device.has_pin ? "Adult PIN changed" : "Adult PIN set"); setPinNew(""); setPinOld(""); refreshDevice(device.mac); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const removePin = async () => {
    try { await api.delete(`/device/${device.mac}/pin`, { data: { pin: pinOld || undefined } }); toast.success("Adult PIN removed"); setPinNew(""); setPinOld(""); refreshDevice(device.mac); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const refreshDevice = async (m) => {
    // Re-verify with the stored Device Key so the playlist source stays visible when no privacy password is set.
    const { data } = await api.post(`/device/verify`, { mac: m, device_key: getDeviceKey() || key });
    setDevice(data);
    const s = await api.get(`/settings`);
    setPlans(s.data.plans || []);
    setCurrency(s.data.currency || "EUR");
    try { const p = await api.get(`/device/${m}/payments`); setPayments(p.data); } catch { /* noop */ }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/device/verify`, { mac, device_key: key });
      setDeviceKey(data.device_key);
      setMac(data.mac);
      setMacL(data.mac);
      await refreshDevice(data.mac);
      toast.success("Device verified");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  const clearPlaylist = async (pid) => {
    await api.delete(`/device/${device.mac}/playlist/${pid}`);
    toast.success("Playlist deleted");
    refreshDevice(device.mac);
  };
  const refreshPlaylist = async (pid) => {
    setLoading(true);
    try { await api.post(`/device/${device.mac}/playlist/${pid}/refresh`); toast.success("Playlist refreshed"); refreshDevice(device.mac); }
    catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setLoading(false); }
  };
  const requestActivation = async (planId) => {
    try {
      const { data } = await api.post(`/device/${device.mac}/activation-request`, { plan_id: planId });
      toast.success(data.message);
      refreshDevice(device.mac);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const reveal = async () => {
    try {
      const { data } = await api.post(`/device/${device.mac}/privacy/verify`, { password: revealPw });
      setDevice(data); setRevealed(true); setRevealPw("");
      toast.success("Playlist details unlocked");
    } catch (e) { toast.error(e.response?.data?.detail || "Wrong privacy password"); }
  };
  const setPrivacy = async () => {
    const pw = window.prompt(device.has_privacy ? "Enter a NEW privacy password (min 4 chars)" : "Set a privacy password (min 4 chars)");
    if (!pw) return;
    let old_password = null;
    if (device.has_privacy) { old_password = window.prompt("Enter your CURRENT privacy password"); if (old_password === null) return; }
    try {
      await api.post(`/device/${device.mac}/privacy`, { password: pw, old_password });
      toast.success(device.has_privacy ? "Privacy password changed" : "Privacy password set");
      setRevealed(false); refreshDevice(device.mac);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const removePrivacy = async () => {
    const pw = window.prompt("Enter your current privacy password to remove it");
    if (pw === null) return;
    try {
      await api.delete(`/device/${device.mac}/privacy`, { data: { password: pw } });
      toast.success("Privacy password removed");
      setRevealed(false); refreshDevice(device.mac);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const locked = device?.has_privacy && !revealed;
  const editDeviceName = async () => {
    const n = window.prompt("Device name (e.g. Living Room TV)", device.name || "");
    if (n === null) return;
    try { await api.post(`/device/${device.mac}/name`, { name: n }); toast.success("Device name saved"); refreshDevice(device.mac); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const input = "w-full px-4 py-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-red-500/50 font-mono-data tracking-wider";
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";

  return (
    <div className="min-h-screen">
      <header className="flex items-center justify-between px-6 md:px-12 h-16 border-b border-zinc-800/50">
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-red-600"><MonitorPlay className="w-4 h-4 text-white" /></div>
          <span className="font-head font-bold">Manage Device</span>
        </button>
        <button onClick={() => navigate("/download")} className="text-sm text-zinc-400 hover:text-white transition-colors">Get the App</button>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {!device ? (
          <div className="glass rounded-2xl p-8 max-w-md mx-auto fade-up">
            <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-5 h-5 text-emerald-400" /><h2 className="font-head text-2xl font-semibold">Sign in to your device</h2></div>
            <p className="text-zinc-500 text-sm mb-6">Enter the MAC Address and Device Key shown on your TV app activation screen.</p>
            <div className="space-y-4">
              <input data-testid="manage-mac-input" className={input} placeholder="MAC Address (e.g. 1A:2B:3C:4D:5E:6F)" value={mac} onChange={(e) => setMacL(e.target.value)} />
              <input data-testid="manage-key-input" className={input} placeholder="Device Key" value={key} onChange={(e) => setKey(e.target.value)} />
              <button data-testid="manage-verify-btn" onClick={verify} disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-semibold transition-colors">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify Device"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6 fade-up">
            {/* Status */}
            <div className="glass rounded-2xl p-6" data-testid="manage-status-card">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-head text-xl font-semibold truncate">{device.name || "Unnamed device"}</h3>
                    <button data-testid="edit-name-btn" onClick={editDeviceName} title="Rename" className="p-1.5 rounded-md bg-zinc-800 hover:bg-red-600 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                  </div>
                  <p className="font-mono-data text-xs text-zinc-500 mt-0.5">{device.mac}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold shrink-0 ${device.status === "active" ? "bg-emerald-500/20 text-emerald-400" : device.status === "trial" ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"}`}>
                  {device.status.toUpperCase()}
                </span>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <div><p className="text-zinc-500">Device Key</p><p className="font-mono-data">{device.device_key}</p></div>
                <div><p className="text-zinc-500">Trial ends</p><p>{fmt(device.trial_expiry)}</p></div>
                <div><p className="text-zinc-500">Activation ends</p><p>{device.activation_expiry ? fmt(device.activation_expiry) : "—"}</p></div>
              </div>
            </div>

            {/* Playlist */}
            <div className="glass rounded-2xl p-6 space-y-4" data-testid="manage-playlist-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-head text-xl font-semibold">Playlist</h3>
                <div className="flex items-center gap-2">
                  {!device.has_privacy ? (
                    <button data-testid="privacy-set-btn" onClick={setPrivacy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-xs font-semibold"><Lock className="w-3.5 h-3.5" /> Set privacy password</button>
                  ) : (<>
                    <button data-testid="privacy-change-btn" onClick={setPrivacy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-xs font-semibold"><Lock className="w-3.5 h-3.5" /> Change</button>
                    <button data-testid="privacy-remove-btn" onClick={removePrivacy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-red-700 text-xs font-semibold"><Unlock className="w-3.5 h-3.5" /> Remove</button>
                  </>)}
                  <button data-testid="manage-add-playlist-btn" onClick={() => setShowForm((v) => !v)} className="px-4 py-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors">
                    {device.playlists?.length ? "Replace / Add" : "Add playlist"}
                  </button>
                </div>
              </div>

              {locked && (
                <div data-testid="privacy-locked" className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <div className="flex items-center gap-2 text-amber-300 text-sm flex-1"><Lock className="w-4 h-4" /> Playlist details are hidden. Enter your privacy password to view or edit them.</div>
                  <div className="flex gap-2">
                    <input data-testid="privacy-reveal-input" type="password" className="px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" placeholder="Privacy password" value={revealPw} onChange={(e) => setRevealPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reveal()} />
                    <button data-testid="privacy-reveal-btn" onClick={reveal} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold"><Eye className="w-4 h-4" /> Unlock</button>
                  </div>
                </div>
              )}

              {device.playlists?.map((p) => (
                <div key={p.id} data-testid={`manage-pl-${p.id}`} className="p-3 rounded-lg bg-zinc-900 border border-zinc-800/50">
                  <div className="flex items-center gap-3">
                    <ListVideo className="w-5 h-5 text-red-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-xs text-zinc-500">TV {p.counts?.live || 0} · Movies {p.counts?.movie || 0} · Series {p.counts?.series || 0} · {p.type.toUpperCase()} · Updated {p.added_at ? fmt(p.added_at) : "—"}</p>
                    </div>
                    <button data-testid={`manage-refresh-${p.id}`} onClick={() => refreshPlaylist(p.id)} className="p-1.5 rounded-md bg-zinc-800 hover:bg-sky-600 transition-colors"><RefreshCw className="w-3.5 h-3.5" /></button>
                    <button data-testid={`manage-delete-${p.id}`} onClick={() => clearPlaylist(p.id)} className="p-1.5 rounded-md bg-zinc-800 hover:bg-red-700 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {p.source ? (
                    <div className="mt-3 pt-3 border-t border-zinc-800/60 text-xs font-mono-data space-y-1 text-zinc-400 break-all" data-testid={`manage-pl-source-${p.id}`}>
                      {p.type === "xtream" ? (<>
                        <div><span className="text-zinc-600">Server: </span>{p.source.url}</div>
                        <div><span className="text-zinc-600">Username: </span>{p.source.username}</div>
                        <div><span className="text-zinc-600">Password: </span>{p.source.password}</div>
                      </>) : (<div><span className="text-zinc-600">M3U URL: </span>{p.source.url}</div>)}
                    </div>
                  ) : locked ? (
                    <div className="mt-3 pt-3 border-t border-zinc-800/60 text-xs text-zinc-600 flex items-center gap-1.5"><Lock className="w-3 h-3" /> URL/credentials hidden</div>
                  ) : null}
                </div>
              ))}
              {showForm && <div className="pt-2"><PlaylistForm mac={device.mac} onDone={() => { setShowForm(false); refreshDevice(device.mac); }} /></div>}
              {!device.playlists?.length && !showForm && <p className="text-zinc-500 text-sm">No playlist yet. Add one so your TV app can load it.</p>}
            </div>

            {/* Parental / Adult PIN */}
            <div className="glass rounded-2xl p-6 space-y-4" data-testid="manage-pin-card">
              <div className="flex items-center gap-2"><Lock className="w-5 h-5 text-amber-400" /><h3 className="font-head text-xl font-semibold">Adult / Parental PIN</h3></div>
              <p className="text-zinc-500 text-sm">Lock adult categories with a 4-digit PIN. Viewers must enter it on the TV to open those groups. {device.has_pin ? "A PIN is currently set." : "No PIN set yet."}</p>
              <div className="flex flex-wrap items-center gap-2">
                {device.has_pin && (
                  <input data-testid="manage-old-pin" type="password" inputMode="numeric" maxLength={4} className="w-32 text-center tracking-[0.4em] font-mono-data px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" placeholder="old PIN" value={pinOld} onChange={(e) => setPinOld(e.target.value.replace(/\D/g, ""))} />
                )}
                <input data-testid="manage-new-pin" type="password" inputMode="numeric" maxLength={4} className="w-32 text-center tracking-[0.4em] font-mono-data px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" placeholder={device.has_pin ? "new PIN" : "PIN"} value={pinNew} onChange={(e) => setPinNew(e.target.value.replace(/\D/g, ""))} />
                <button data-testid="manage-save-pin" onClick={savePin} className="px-4 py-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-semibold">{device.has_pin ? "Change PIN" : "Set PIN"}</button>
                {device.has_pin && <button data-testid="manage-remove-pin" onClick={removePin} className="px-4 py-2 rounded-full bg-zinc-800 hover:bg-red-700 text-sm font-semibold flex items-center gap-1.5"><Unlock className="w-3.5 h-3.5" /> Remove</button>}
              </div>
            </div>

            {/* Activation */}
            <div className="glass rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-red-500" /><h3 className="font-head text-xl font-semibold">Activation</h3></div>
              <p className="text-zinc-500 text-sm">Choose a plan to activate your device after the trial. Your request is sent to the admin and activated once payment is confirmed.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {plans.map((pl) => (
                  <div key={pl.id} data-testid={`plan-${pl.id}`} className="p-5 rounded-xl bg-zinc-900 border border-zinc-800/50">
                    <p className="font-head text-lg font-semibold">{pl.name}</p>
                    <p className="text-2xl font-bold mt-1">{pl.price} <span className="text-sm text-zinc-500">{pl.currency || currency}</span></p>
                    <p className="text-xs text-zinc-500 mt-1">{pl.months === 0 ? "Lifetime" : `${pl.months} months`}</p>
                    <button data-testid={`activate-plan-${pl.id}`} onClick={() => requestActivation(pl.id)}
                      className="mt-3 w-full py-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors">Request activation</button>
                  </div>
                ))}
              </div>
              {payments.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">History</p>
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm py-1.5 border-t border-zinc-800/50">
                      <span>{p.plan}</span>
                      <span className={`flex items-center gap-1.5 ${p.status === "paid" ? "text-emerald-400" : p.status === "pending" ? "text-amber-400" : "text-red-400"}`}>
                        {p.status === "paid" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />} {p.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
